import React, { useEffect, useState } from "react";
import styles from './LogReports.module.scss';
import PageTitle from "../../../components/pageTitle/PageTitle";
import Filter from "../../../components/filter/Filter";
import Button from "../../../components/button/Button";
import LogReportCard from "../../../components/logReportCard/LogReportCard";
import { useToasts } from "../../../contexts/ToastContext";
import axiosClient from "../../../api/axiosClient";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import { format } from "date-fns";
import LoadMore from "../../../components/loadMore/loadMore";
import Modal from "../../../components/modal/Modal";
import DropDownButton from "../../../components/dropDownButton/DropDownButton";
import CustomDateRangePicker from "../../../components/customDateRangePicker/CustomDateRangePicker";
import { useCreateLogReport } from "../../../hooks/useCreateLogReport";

const LogReports = () => {
  const limit = 20;
  const [selectedFilter, setSelectedFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [logReportId, setLogReportId] = useState(null);
  const [refetchTrigger, setRefetchTrigger] = useState(false);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 6)));
  const [endDate, setEndDate] = useState(new Date());
  const [dateRange, setDateRange] = useState({ startDate, endDate });
  const { showToast } = useToasts();
  const { createLogReport, isLoading: isCreatingLogReport } = useCreateLogReport();

  useEffect(() => {
    if(startDate && endDate) {
      setDateRange({ startDate, endDate });
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const fetchData = async () => {
      if(page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadMoreLoading(true);
      }
      const params = {
        limit: limit,
        page: page,
        startDate: format(dateRange.startDate, "yyyy-MM-dd"),
        endDate: format(dateRange.endDate, "yyyy-MM-dd"),
      };
      if(selectedFilter) {
        params.filter = selectedFilter;
      }
      await axiosClient.get(`log-reports`, {
        params: params
      })
      .then ((response) => {
        if(response.data?.logReports) {
          if(page === 1) {
            setReports([]);
          }
          setReports((prevData) => {
            return [...prevData, ...response.data.logReports.map((report) => {
              return {
                id: report.id,
                date: report.date,
                data: [
                  {
                    label: 'Report ID',
                    text: report.id
                  },
                  {
                    label: 'Log Date',
                    text: format(report.date, 'MM/dd/yyyy')
                  },
                  {
                    label: 'Type',
                    text: report.type
                  },
                ],
                cta: report.status !== 'Deleted' ? {
                  label: report.type === 'Clinical' ? 
                    (report.status === 'Submitted' ? 'View Cases' : 'Complete the Case Log') :
                    'View Details',
                  link: report.type === 'Clinical' ? 
                    `/log-reports/${report.id}/case-logs` : 
                    `/log-reports/${report.id}/reading-day`
                } : null,
                status: report.status,
                type: report.type
              }
            })];
          });
          setTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadMoreLoading(false);
      });
    };

    fetchData();
  }, [page, selectedFilter, showToast, dateRange, refetchTrigger]);

  const loadMore = async () => {
    setPage(page + 1);
  };



  const handleDelete = async () => {
    setIsDeleteLoading(true);
    await axiosClient.delete(`log-reports`, {
      params: {
        id: logReportId
      }
    })
    .then((response) => {
      setShowModal(false);
      showToast(response.message);
      setRefetchTrigger(prev => !prev);
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsDeleteLoading(false);
    });
  };

  return (
    <>
      {(isLoading || isCreatingLogReport) && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle
          label="Log Reports"
        />
      </div>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <div className={styles.filter}>
            <Filter
              filters={[
                {
                  label: 'All',
                  value: '',
                },
                {
                  label: 'Pending',
                  value: 'Pending',
                },
                {
                  label: 'Submitted',
                  value: 'Submitted',
                },
                {
                  label: 'Reading Days',
                  value: 'ReadingDay',
                }
              ]}
              selected={selectedFilter}
              onChange={(filter) => {
                setSelectedFilter(filter);
              }}
            />
          </div>
          <CustomDateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
          <DropDownButton
            label="Start&nbsp;Log&nbsp;Report"
            disabled={isLoading || isCreatingLogReport}
            items={[
              {
                label: 'Clinical Report',
                onClick: () => createLogReport(false)
              },
              {
                label: 'Reading Day',
                onClick: () => createLogReport(true)
              }
            ]}
          />
        </div>
        { reports.length > 0 ? <div className={styles.cardList}>
          {reports.map((report, index) => {
            return (
              <div className={styles.cardItem} key={`report-${index}`}>
                <LogReportCard
                  {...report}
                  onDelete={() => {
                    setLogReportId(report.id);
                    setShowModal(true);
                  }}
                />
              </div>
            );
          })}
          {(isLoadMoreLoading || page < totalPages) 
            && <LoadMore isLoading={isLoadMoreLoading} onClick={loadMore} />}
        </div> : !isLoading && <div className={styles.emptyReports}>
          <p>You have no log reports at this time.</p>
        </div>}
      </div>
      {showModal && <Modal>
        <div className={styles.deleteModal}>
          {isDeleteLoading && <LoadingOverlay />}
          <h2 className={styles.modalTitle}>This action cannot be undone. Are you sure you want to delete this report?</h2>
          <div className={styles.buttons}>
            <Button
              label="Delete"
              size="small"
              variant="secondary"
              onClick={() => {
                handleDelete();
              }}
            />
            <Button
              label="Cancel"
              size="small"
              variant="secondary"
              onClick={() => setShowModal(false)}
            />
          </div>
        </div>
      </Modal>}
    </>
  );
};

export default LogReports;